import React, { useEffect, useState } from "react";
import {
  ChevronRight,
  Calendar,
  RotateCcw,
  Clock,
  Truck,
  XCircle,
  BadgeCheck,
  Loader2,
  PackageCheck,
  FileText,
  Eye,
  Download,
  Package,
  CheckCircle,
  Star,
  Info,
} from "lucide-react";
import { Tooltip } from "@mui/material";
import { ORDER_STATUSES, PAYMENT_STATUSES } from "../../constants/order-status";
import BaseApi from "../../services/base-api";
import { Order } from "../../types/Order";
import { ImageWithFallback } from "../../constants/image-placeholder";
import Price from "../Currency/Price";
import { useTranslation } from "react-i18next";
import LoadingComponent from "../LoadingComponent";
import Pagination from "../Pagination";
import { usePagination } from "../../hooks/usePagination";
import { Money } from "@mui/icons-material";
import ProductRating from "../Product/rating";

interface OrdersListProps {
  onSelectOrder: (order: Order) => void;
  onDownloadInvoice?: (orderId: string) => void;
  onViewInvoice?: (orderId: string) => void;
  invoiceLoading?: string | null;
}

interface InvoiceInfo {
  [orderId: string]: {
    id: string;
    invoiceNumber: string;
    status: string;
    amount: number;
  } | null;
}

const OrdersList: React.FC<OrdersListProps> = ({
  onSelectOrder,
  onDownloadInvoice,
  onViewInvoice,
  invoiceLoading,
}) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [invoiceInfo, setInvoiceInfo] = useState<InvoiceInfo>({});
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const { t } = useTranslation();

  const {
    currentPage,
    pageSize,
    totalPages,
    totalItems,
    setPage,
    setPageSize,
    setTotalItems,
  } = usePagination({ initialPageSize: 6 });

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await BaseApi.get(
        `/orders/my-orders?page=${currentPage}&limit=${pageSize}`,
      );
      if (res.success) {
        setOrders(res.data);
        setTotalItems(res.pagination?.totalItems || 0);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage, pageSize]);

  const fetchInvoiceInfo = async (orders: Order[]) => {
    const info: InvoiceInfo = {};

    for (const order of orders) {
      try {
        const res = await BaseApi.get(`/invoices/order/${order.id}`);
        if (res.success && res.data) {
          info[order.id] = res.data;
        } else {
          info[order.id] = null;
        }
      } catch (error) {
        info[order.id] = null;
      }
    }
    setInvoiceInfo(info);
  };

  const getStatusColor = (status: Order["status"]) => {
    return ORDER_STATUSES[status]?.color;
  };

  const getPaymentStatusColor = (status: Order["paymentStatus"]) => {
    return PAYMENT_STATUSES[status]?.color || "bg-gray-100 text-gray-800";
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case "pending":
        return {
          icon: Clock,
          color: "bg-yellow-100 text-yellow-800 border-yellow-200",
          label: t("Pending"),
        };
      case "confirmed":
        return {
          icon: CheckCircle,
          color: "bg-[#0d223f]-100 text-blue-800 border-[#0d223f]",
          label: t("Confirmed"),
        };
      case "processing":
        return {
          icon: Package,
          color: "bg-purple-100 text-purple-800 border-purple-200",
          label: t("Processing"),
        };
      case "shipped":
        return {
          icon: Package,
          color: "bg-indigo-100 text-indigo-800 border-indigo-200",
          label: t("Shipped"),
        };
      case "delivered":
        return {
          icon: CheckCircle,
          color: "bg-green-100 text-green-800 border-green-200",
          label: t("Delivered"),
        };
      case "cancelled":
        return {
          icon: XCircle,
          color: "bg-red-100 text-red-800 border-red-200",
          label: t("Cancelled"),
        };
      default:
        return {
          icon: Package,
          color: "bg-gray-100 text-gray-800 border-gray-200",
          label: status,
        };
    }
  };

  const getPaymentStatusIcon = (status: Order["paymentStatus"]) => {
    const icons = {
      paid: <Money className="w-5 h-5 text-green-500" />,
      unpaid: <Money className="w-5 h-5 text-orange-500" />,
      failed: <Money className="w-5 h-5 text-red-500" />,
      pending: <Money className="w-5 h-5 text-yellow-500" />,
    } as {
      [key: string]: React.ReactNode;
    };
    return icons[status];
  };

  const getStatusIcon = (status: Order["status"] | Order["paymentStatus"]) => {
    const icons = {
      pending: <Clock className="w-5 h-5" />,
      confirmed: <BadgeCheck className="w-5 h-5" />,
      processing: <Loader2 className="w-5 h-5 animate-spin" />,
      shipped: <Truck className="w-5 h-5" />,
      delivered: <PackageCheck className="w-5 h-5" />,
      cancelled: <XCircle className="w-5 h-5" />,
      refunded: <RotateCcw className="w-5 h-5" />,
    } as {
      [key: string]: React.ReactNode;
    };
    return icons[status];
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const calculateTotal = (order: Order) => {
    const subtotal = order.items.reduce(
      (sum, item) => sum + Number(item.total || 0),
      0,
    );
    const shippingFee = Number(order.shippingFee || 0);
    const tax = Number(order.tax || 0);
    const discount = Number(order.discount || 0);
    return subtotal + shippingFee + tax - discount;
  };

  const canRateOrder = (order: Order) => {
    // Can rate if order is delivered
    return order.status === "delivered";
  };

  const toggleExpandOrder = (orderId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  // Loading State
  if (loading) {
    return <LoadingComponent title={t("Orders")} />;
  }

  if (orders.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
        <Package className="w-16 h-16 text-gray-400 mx-auto" />
        <h3 className="mt-4 text-xl font-semibold text-gray-900">
          {t("No orders yet")}
        </h3>
        <p className="mt-2 text-gray-600">
          {t("Your orders will appear here once you make a purchase")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {orders.map((order, index) => {
        const total = calculateTotal(order);
        const invoice = invoiceInfo[order.id];
        const isExpanded = expandedOrderId === order.id;
        const canRate = canRateOrder(order);

        return (
          <div
            key={order.id}
            className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 transform hover:shadow-xl"
            style={{
              animation: `fadeIn 0.5s ease-out ${index * 0.1}s backwards`,
            }}
          >
            {/* Order Header */}
            <div
              onClick={() => onSelectOrder(order)}
              className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 sm:px-6 py-3 border-b-2 border-gray-200 cursor-pointer hover:from-gray-100 hover:to-gray-200 transition-all"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-base sm:text-lg font-bold text-gray-800">
                    {order.orderNumber}
                  </h3>
                  <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border-2 ${getStatusColor(
                      order.status,
                    )}`}
                  >
                    {getStatusIcon(order.status)}
                    <span className="capitalize">{order.status}</span>
                  </div>
                  <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border-2 ${getPaymentStatusColor(
                      order.paymentStatus,
                    )}`}
                  >
                    {getPaymentStatusIcon(order.paymentStatus)}
                    {order.paymentStatus === "failed" && (
                      <Tooltip
                        title={order.paymentFailureReason || t("Payment Failed")}
                        placement="top"
                      >
                        <Info
                          className="w-4 h-4 text-red-500 cursor-help"
                          aria-label={t("Payment failure reason")}
                        />
                      </Tooltip>
                    )}
                    <span className="capitalize">{order.paymentStatus}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(order.createdAt)}</span>
                </div>
              </div>
            </div>

            {/* Invoice Section */}
            {invoice && (
              <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-[#0d223f]">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-semibold text-gray-900">
                        {t("Invoice")} #{invoice.invoiceNumber}
                      </p>
                      <p className="text-xs text-gray-600">
                        {invoice.status === "paid" ? (
                          <span className="text-green-600 font-medium">
                            {t("Paid")}
                          </span>
                        ) : (
                          <span className="text-yellow-600 font-medium">
                            {t("Pending")}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {onViewInvoice && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewInvoice(order.id);
                        }}
                        disabled={invoiceLoading === order.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-[#0d223f] text-blue-700 rounded-lg hover:bg-[#0d223f] transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        {invoiceLoading === order.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                        {t("View")}
                      </button>
                    )}
                    {onDownloadInvoice && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDownloadInvoice(order.id);
                        }}
                        disabled={invoiceLoading === order.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0d223f] text-white rounded-lg hover:bg-[#0d223f] transition-colors text-sm font-medium disabled:opacity-50"
                      >
                        {invoiceLoading === order.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        {t("Download")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Order Content */}
            <div className="p-4 sm:p-6">
              {/* Product Images Preview */}
              <div className="flex items-start gap-3 mb-4">
                <div className="flex -space-x-2 flex-shrink-0">
                  {order.items.slice(0, 3).map((item, idx) => (
                    <div
                      key={item.id}
                      className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl border-3 border-white shadow-md overflow-hidden"
                      style={{ zIndex: 3 - idx }}
                    >
                      <ImageWithFallback
                        src={item.product?.images?.[0]}
                        alt={item.productName.en}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                  {order.items.length > 3 && (
                    <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl border-3 border-white shadow-md bg-gray-800 flex items-center justify-center">
                      <span className="text-white font-bold text-sm">
                        +{order.items.length - 3}
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-600 mb-2">
                    {order.items.length} {t("item")}
                    {order.items.length > 1 ? "s" : ""}
                  </p>
                  {/* Display items point-wise */}
                  <ul className="space-y-1">
                    {order.items.slice(0, 3).map((item) => (
                      <li
                        key={item.id}
                        className="text-xs text-gray-500 truncate"
                      >
                        • {item.productName.en}
                      </li>
                    ))}
                    {order.items.length > 3 && (
                      <li className="text-xs text-gray-500">
                        {t("and")} {order.items.length - 3} {t("more items")}
                      </li>
                    )}
                  </ul>
                </div>
                {/* Rate Products Button - Show only for delivered orders */}
                {canRate && (
                  <div className="mb-4">
                    <button
                      onClick={(e) => toggleExpandOrder(order.id, e)}
                      className="flex items-center justify-center gap-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-3 rounded-xl hover:shadow-lg transition-all font-semibold text-sm"
                    >
                      <Star className="w-5 h-5" />
                      {isExpanded ? t("Hide Products") : t("Rate Products")}
                    </button>
                  </div>
                )}
              </div>

              {/* Expanded Products Section with Rating */}
              {isExpanded && canRate && (
                <div className="mt-4 space-y-4 border-t-2 border-gray-200 pt-4">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">
                    {t("Rate Your Products")}
                  </h4>
                  {order.items.map((item) => (
                    <div
                      key={item.id}
                      className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 border-2 border-gray-200"
                    >
                      <div className="flex items-start gap-4 mb-3">
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                          <ImageWithFallback
                            src={item.product?.images?.[0]}
                            alt={item.productName.en}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 text-base mb-1">
                            {item.productName.en}
                          </h5>
                          <p className="text-sm text-gray-600">
                            {t("Quantity")}: {item.quantity}
                          </p>
                          <p className="text-sm font-bold text-gray-900 mt-1">
                            <Price amount={item.total} />
                          </p>
                        </div>
                      </div>

                      {/* Rating Component */}
                      <ProductRating
                        product={{
                          id: item.productId,
                          name: item.productName,
                          rating: item.product?.rating,
                          ratingCount: item.product?.ratingCount,
                        }}
                        orderId={order.id}
                        showReviews={false}
                        allowRating={true}
                        mode="full"
                        size="md"
                        showCount={false}
                        onRatingUpdate={(rating) => {
                          // console.log("Product rated:", item.productId, rating);
                          // Optionally refresh orders to show updated ratings
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Order Footer */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-600 mt-4">
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">
                    {t("Order Total")}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-800">
                    <Price amount={Number(total || 0)} />
                  </p>
                </div>

                <button
                  onClick={() => onSelectOrder(order)}
                  className="flex items-center gap-2 text-gray-600 hover:text-black transition-colors"
                >
                  <span className="text-sm font-semibold hidden sm:inline">
                    {t("View Details")}
                  </span>
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
      {orders.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[10, 20, 50, 100]}
          showPageSizeSelector={true}
          isLoading={loading}
        />
      )}
    </div>
  );
};

export default OrdersList;
